import { yupResolver } from "@hookform/resolvers/yup";
import moment from "moment";
import React, { createRef, forwardRef, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import {
  View,
  StyleSheet,
  ScrollView,
  useWindowDimensions,
  Platform,
  TouchableOpacity,
  Dimensions,
} from "react-native";
import {
  BottomSheet,
  theme,
  Text,
  typography,
  Button,
  Modal as ModalComponent,
  TextInput,
  ITextInput,
  Dropdown,
  Checkbox,
  ErrorMessage,
  ErrorWarningBottomSheet,
  Icon,
} from "ui-components";
import { CreditCardFields } from "../../../enums";
import { translate } from "../../../i18n/translate";
import { addCreditCardSchema } from "../../../screens/new/utils/validation-schemas-payment";
import { MonthYearDropdownNew } from "../dropdown/new-month-year-dropdown";
import { customAlphabet } from "nanoid/non-secure";
import { DomusoApiService } from "../../../api/DomusoApiService";
import { VantivEProtectService } from "../../../api/VantivEProtectService";
import { useDispatch, useSelector } from "react-redux";
import { setDefaultPaymentSourceUpdated } from "../../../redux/actions/actionCreators";
import { RootState } from "../../../redux/reducers";
import { getErrorMessageId, ERROR_CODES } from "../../../utils/errorCodes";
import { ApiError } from "../../../utils/error";
import { templateAddSupportLink } from "../../../utils/languageTemplating";
import automationTestingLocators from "../../../screens/new/utils/automation-testing-locators.json";
import Auryc from "rn-auryc-sdk";

const nanoid = customAlphabet("1234567890", 18);

const { StateAbbrevations } = require("domuso-constants").states;

const webMaxWidth = 1200;
const tabMaxWidth = 992;
const mobileWebMaxWidth = 768;
const mobileMaxWidth = 576;

const {
  CARD_HOLDER,
  CARD_NUMBER,
  CVV,
  STREET_ADDRESS,
  STREET_ADDRESS_2,
  CITY,
  STATE,
  EXP_DATE,
  ZIP,
  NICKNAME,
} = CreditCardFields;

interface AddPaymentCCDebitProps {
  height?: number | string;
  children?: any;
  screenWidth?: number;
  reservationId?: any;
  isDomusoCash?: any;
  fromSelectPayment?: any;
  updated?: any;
  toggleForCardBottomSheet: Boolean;
  showStoreCardCheckbox?: boolean;
  experianLinkCohort?: boolean;
}

export const AddPaymentCCDebit = forwardRef(
  (
    {
      height,
      reservationId,
      isDomusoCash,
      updated,
      toggleForCardBottomSheet,
      experianLinkCohort,
      showStoreCardCheckbox = true,
    }: AddPaymentCCDebitProps,
    ref: any
  ) => {
    const dispatch = useDispatch();
    const errorPopup = useRef();
    const abortController = useRef<AbortController>(null);

    const { width: screenWidth } = useWindowDimensions();
    const { height: screenHeight } = Dimensions.get("window");
    const [currentInputIndex, setCurrentInputIndex] = useState(0);
    const [cardNumber, setCardNumber] = useState("");
    const [showAddBillAddress, setShowAddBillAddress] = useState(false);
    const [selectedState, setSelectedStateValue] = useState();
    const [loading, setLoading] = useState(false);
    const [shouldStoreCard, setShouldStoreCard] = useState(true);
    const [tmId, setTmId] = useState(null);
    const [errorMsgPopup, setErrorMsgPopup] = useState<any>(false);

    const cognitoUser = useSelector((state) => state.auth.cognitoUser);
    const logger = useSelector((state: RootState) => state.logger);

    let CustomTag = screenWidth <= mobileMaxWidth ? BottomSheet : ModalComponent;
    const styles = StyleSheet.create({
      cardInformationRow: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
      },
      checkboxStyle: {
        width: 20,
        height: 20,
      },
      checkboxTextStyle: {
        ...typography.title2,
        color: theme.colors.text3,
        marginLeft: 10,
        flex: 1,
      },
      label: { fontFamily: "Lato_700Bold", color: theme.colors.darkerGrey },
      indicatorStyle: {
        width: 12,
        height: 12,
        borderRadius: 6,
        backgroundColor: theme.colors.text2,
        margin: 6,
        padding: 6,
      },
      indicatorContainer: {
        alignItems: "center",
        margin: 6,
        flexDirection: "row",
        justifyContent: "center",
      },
    });
    const service = new DomusoApiService(cognitoUser, dispatch);
    const vantivService = new VantivEProtectService(cognitoUser, dispatch);

    useEffect(() => {
      setTmId(`domus-${nanoid()}`);
    }, []);

    const {
      register,
      trigger,
      handleSubmit,
      getValues,
      setValue: originalSetValue,
      setError,
      clearErrors,
      reset: resetOriginal,
      formState: { errors },
    } = useForm({
      mode: "all",
      resolver: yupResolver(addCreditCardSchema),
    });
    const inputAccessoryViewID = "inputAccessoryViewID4";

    const textInputRef = [
      createRef(),
      createRef(),
      createRef(),
      createRef(),
      createRef(),
      createRef(),
      createRef(),
      createRef(),
      createRef(),
    ];
    useEffect(() => {
      register(CARD_HOLDER);
      register(CARD_NUMBER);
      register(CVV);
      register(STREET_ADDRESS);
      register(STREET_ADDRESS_2);
      register(CITY);
      register(STATE);
      register(EXP_DATE);
      register(ZIP);
    }, [register]);

    const reset = () => {
      setShowAddBillAddress(false);
      setSelectedStateValue(undefined);
      setCardNumber("");
      resetOriginal();
    };
    useEffect(() => {
      /*
        reset form values every time bottom-sheet opens
      */
      setLoading(false);
      reset();
    }, [toggleForCardBottomSheet]);

    const formatCardNumber = (value: string) => {
      const regex = /^(\d{0,4})(\d{0,4})(\d{0,4})(\d{0,4})$/g;
      const onlyNumbers = value.replace(/[^\d]/g, "");

      return onlyNumbers.replace(regex, (regex, $1, $2, $3, $4) =>
        [$1, $2, $3, $4].filter((group) => !!group).join(" ")
      );
    };
    const setValue = (formName: string, val: any) => {
      clearErrors(formName);
      if (formName == CARD_NUMBER) {
        originalSetValue(formName, formatCardNumber(val));
        setCardNumber(formatCardNumber(val));
        return;
      }
      originalSetValue(formName, val);
    };
    // Should this be in redux?
    const onSubmit = async (data: any) => {
      setLoading(true);
      try {
        setErrorMsgPopup(null);

        const payload = {
          ...data,
          reservationId,
          allowCof: !!shouldStoreCard,
          paymentSourceType: "CREDIT_CARD",
          cvv: data.cvv,
          threatMetrixSessionId: tmId,
        };

        if (experianLinkCohort) {
          const { paypageRegistrationId, lastFour, bin, type } =
            await vantivService.createPaypageRegistration(payload.cardNumber, payload.cvv);

          payload.last4 = lastFour ? lastFour : payload?.cardNumber?.toString().slice(-4);
          delete payload.cardNumber;
          delete payload.cvv;

          payload.paypageRegistrationId = paypageRegistrationId;

          payload.bin = bin;
          payload.type = type;
        }

        if (isDomusoCash) {
          payload.paymentType = "DOMUSO_CASH";
        }
        abortController.current = new AbortController();
        const { payment_source_id: paymentSourceId } = await service.addPaymentSource(
          payload,
          abortController?.current?.signal
        );
        ref?.current?.close();
        updated();
        dispatch(setDefaultPaymentSourceUpdated(paymentSourceId));
        reset();
        return;
      } catch (apiError) {
        //Only call reset() were need it users should be able to retry without filling all info again.
        logger.error(apiError);
        const { code } = apiError;
        setErrorMsgPopup(null);
        if (apiError?.message == "Aborted") {
          reset();
          setLoading(false);
          return;
        }
        let errorMsg: string | null = null;
        if (code === ERROR_CODES.EXPERIAN_LINK_NO_NAME_MATCH) {
          errorMsg = translate("addPaymentCCDebit.errors.cardholderVerificationError");
        } else if (
          [
            ERROR_CODES.CREDIT_CARD_INVALID_BILLING_ADDRESS_ERROR,
            ERROR_CODES.CREDIT_CARD_INVALID_BILLING_ZIP_ERROR,
          ].includes(code)
        ) {
          setErrorMsgPopup(translate("addPaymentCCDebit.avs.generic"));
          errorPopup?.current?.open();
          setLoading(false);
          return;
          // for experian we only care about code 6001 returned from backend.
          // for that reason we only show that error
          // ERROR_CODES.EXPERIAN_LINK_REQUEST_FAIL (code 6000) is still treated as success since it is
          // not returned from the backend
        } else if (code === ERROR_CODES.EXPERIAN_LINK_NO_NAME_MATCH) {
          setErrorMsgPopup(translate("errors.experianLinkNoNameMatch"));
          errorPopup?.current?.open();
          setLoading(false);
          return;
        } else if (code === ERROR_CODES.SCHEMA_VALIDATION_ERROR) {
          //Missing required fields in request
          errorMsg = apiError.message;
        }
        if (errorMsg) {
          setErrorMsgPopup(errorMsg);
          errorPopup?.current?.open();
          setLoading(false);
          return;
        }
        const blockedRes = await service.checkUserCCPayBlock({ reservationId }).catch((e) => {
          logger.error(e);
          return { blocked: false };
        });
        if (blockedRes?.blocked) {
          setLoading(false);
          // return ref?.current?.close();
        }
        // Get error message to render from the apiError.
        let errorMessage = _getApiErrorMessageJSX(apiError);
        // Regardless of the error will take user to first step in form.
        // State should not be cleared
        setShowAddBillAddress(false);
      }
      setLoading(false);
    };

    /**
     *
     * @param apiError: Api response error from backend.
     * @return JSX component for API error banner to be rendered.
     */
    const _getApiErrorMessageJSX = (apiError: ApiError): JSX.Element => {
      let errorMessage: string = "";
      // Get message ID to be used for translation if applicable.
      const messageId = getErrorMessageId(apiError);
      if (messageId) {
        errorMessage = translate("addPaymentCCDebit." + messageId) || "";
      } else {
        // Check if no translation available and message present then show message.
        if (apiError.message) {
          // There is a chance that the error message does not have a code.
          if (
            apiError?.message == "Failed to fetch" ||
            apiError?.message == "Network request failed"
          ) {
            errorMessage = translate("addPaymentCCDebit.connectionErrorMessage") as string;
          } else {
            errorMessage = apiError.message;
          }
          // console.log("Error message fetched from backend");
        } else {
          errorMessage = translate("addPaymentCCDebit.unexpectedError");
          // console.log("Using the default error message");
        }
      }
      setErrorMsgPopup(errorMessage);
      errorPopup?.current?.open();

      // Format message template.
      // Contact support link to add to error message if required.
      const errorWithSupportLink = templateAddSupportLink(errorMessage);
      const errorMessageJSX: JSX.Element = <Text>{errorWithSupportLink}</Text>;

      return errorMessageJSX;
    };

    const cancelRequest = () => abortController.current && abortController.current.abort();
    return (
      <CustomTag
        ref={ref}
        height={height || 620}
        showCloseIcon={true}
        containerStyle={{
          width:
            screenWidth <= mobileMaxWidth
              ? "100%"
              : screenWidth >= mobileMaxWidth && screenWidth <= webMaxWidth
              ? 400
              : 500,
        }}
        onDismiss={() => {
          cancelRequest();
        }}
        {...automationTestingLocators.addPaymentCCDebit.popup}>
        <View
          style={screenWidth <= mobileMaxWidth ? { flex: 1 } : { maxHeight: screenHeight - 100 }}>
          <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={{ flexGrow: 1 }}>
            <Text
              style={[
                typography.title1,
                { fontFamily: "Lato_900Black", color: theme.colors.secondary },
              ]}
              {...automationTestingLocators.addPaymentCCDebit.heading}>
              {translate("addPaymentCCDebit.title")}
            </Text>
            <View style={{ margin: 2 }} />
            <Icon name={"credit-card-group"} height={24} width={104} />
            {!showAddBillAddress ? (
              <>
                <View style={{ margin: 6 }} />
                <View>
                  <LongRow
                    value={cardNumber}
                    styles={styles}
                    errors={errors}
                    keyboardType="number-pad"
                    setValue={setValue}
                    maxLength={19}
                    formName={CARD_NUMBER}
                    placeholder="1234 1234 1234 1234"
                    inputAccessoryViewID={inputAccessoryViewID}
                    ref={textInputRef[0] as any}
                    onFocus={() => setCurrentInputIndex(0)}
                    onSubmitEditing={() => {}}
                    dataSet={{ heapRedactText: "" }}
                    isSensitiveInformation={true}
                    {...automationTestingLocators.addPaymentCCDebit.cardNumber}
                  />
                </View>
                <View style={{ margin: 6 }} />

                <CardInformationRow
                  cvvValue={getValues(CVV)}
                  styles={styles}
                  setValue={setValue}
                  setError={setError}
                  expValue={getValues(EXP_DATE)}
                  errors={errors}
                  clearErrors={clearErrors}
                  inputAccessoryViewID={inputAccessoryViewID}
                  ref={[textInputRef[1], textInputRef[2]] as any}
                  onFocus0={() => setCurrentInputIndex(2)}
                  onSubmitEditing0={() => (textInputRef[2].current as any).focus()}
                  onFocus1={() => setCurrentInputIndex(3)}
                  onSubmitEditing1={() => (textInputRef[3].current as any).focus()}
                  dataSet={{ heapRedactText: "" }}
                />
                <View style={{ margin: 6 }} />

                <LongRow
                  value={getValues(CARD_HOLDER)}
                  styles={styles}
                  setValue={setValue}
                  errors={errors}
                  formName={CARD_HOLDER}
                  placeholder="e.g: Alex Perry"
                  inputAccessoryViewID={inputAccessoryViewID}
                  ref={textInputRef[3] as any}
                  onFocus={() => setCurrentInputIndex(4)}
                  onSubmitEditing={() => (textInputRef[4].current as any).focus()}
                  returnKeyType="next"
                  isSensitiveInformation={false}
                  {...automationTestingLocators.addPaymentCCDebit.cardholder}
                />
                {showStoreCardCheckbox && <View style={{ margin: 6 }} />}
                {showStoreCardCheckbox && (
                  <View style={{ flexDirection: "row" }}>
                    <View style={{ paddingTop: 3 }}>
                      <Checkbox
                        style={styles.checkboxStyle}
                        onValueChange={(newValue: boolean) => {
                          setShouldStoreCard(newValue);
                        }}
                        value={shouldStoreCard}
                        {...automationTestingLocators.addPaymentCCDebit.storeCreditCardCheckbox}
                      />
                    </View>
                    <Text
                      style={styles.checkboxTextStyle}
                      {...automationTestingLocators.addPaymentCCDebit.storeCreditCardText}>
                      {`${translate("addPaymentCCDebit.storeCardText")}`}
                    </Text>
                  </View>
                )}

                {shouldStoreCard && (
                  <>
                    <View style={{ margin: 8 }} />
                    <LongRow
                      value={getValues(NICKNAME)}
                      styles={styles}
                      setValue={setValue}
                      errors={errors}
                      formName={NICKNAME}
                      placeholder="e.g: Chase Credit Card"
                      inputAccessoryViewID={inputAccessoryViewID}
                      ref={textInputRef[4] as any}
                      onFocus={() => setCurrentInputIndex(4)}
                      onSubmitEditing={() => {}}
                      returnKeyType="next"
                      maxLength={27}
                      isSensitiveInformation={false}
                      {...automationTestingLocators.addPaymentCCDebit.nickname}
                    />
                  </>
                )}
              </>
            ) : (
              <>
                <View style={{ margin: 6 }} />
                <View />
                <LongRow
                  value={getValues(STREET_ADDRESS)}
                  styles={styles}
                  errors={errors}
                  setValue={setValue}
                  formName={STREET_ADDRESS}
                  inputAccessoryViewID={inputAccessoryViewID}
                  ref={textInputRef[5] as any}
                  onFocus={() => setCurrentInputIndex(5)}
                  onSubmitEditing={() => (textInputRef[6].current as any).focus()}
                  returnKeyType="next"
                  placeholder="e.g: 123 Main Street"
                  {...automationTestingLocators.addPaymentCCDebit.streetAddress}
                />
                <View style={{ margin: 6 }} />

                <LongRow
                  value={getValues(STREET_ADDRESS_2)}
                  styles={styles}
                  errors={errors}
                  setValue={setValue}
                  formName={STREET_ADDRESS_2}
                  inputAccessoryViewID={inputAccessoryViewID}
                  ref={textInputRef[6] as any}
                  onFocus={() => setCurrentInputIndex(6)}
                  onSubmitEditing={() => (textInputRef[7].current as any).focus()}
                  returnKeyType="next"
                  placeholder="e.g: APT 124"
                  {...automationTestingLocators.addPaymentCCDebit.streetAddress2}
                />
                <View style={{ margin: 6 }} />

                <LongRow
                  value={getValues(CITY)}
                  styles={styles}
                  errors={errors}
                  setValue={setValue}
                  formName={CITY}
                  inputAccessoryViewID={inputAccessoryViewID}
                  ref={textInputRef[7] as any}
                  onFocus={() => setCurrentInputIndex(7)}
                  // onSubmitEditing={() => (ref[7].current as any).focus()}
                  returnKeyType="next"
                  placeholder="Enter City Name"
                  {...automationTestingLocators.addPaymentCCDebit.city}
                />
                <View style={{ margin: 6 }} />

                <View style={{ flexDirection: "row" }}>
                  <View style={{ flex: 1, marginRight: 4 }}>
                    <Dropdown
                      textInputProps={{
                        labelStyle: { fontFamily: "Lato_700Bold" },
                        error: errors?.[STATE],
                      }}
                      placeholder={`${translate("addPaymentCCDebit.stateDropdown")}`}
                      label="State"
                      pickerItem={StateAbbrevations.map((value) => ({ value, label: value }))}
                      onValueChange={(value, index) => {
                        setSelectedStateValue(value);
                        setValue(STATE, value);
                      }}
                      selectedValue={selectedState}
                      labelStyle={styles.label}
                      {...automationTestingLocators.addPaymentCCDebit.state}
                    />
                    {Platform.OS !== "ios" && (
                      <>
                        <View style={{ marginLeft: -8 }}>
                          <ErrorMessage
                            errors={errors}
                            style={{ fontFamily: "Lato_700Bold" }}
                            formName={STATE}
                          />
                        </View>
                      </>
                    )}
                  </View>
                  <View style={{ flex: 1, marginLeft: 4 }}>
                    <LongRow
                      value={getValues(ZIP)}
                      keyboardType="number-pad"
                      errors={errors}
                      styles={styles}
                      setValue={setValue}
                      formName={ZIP}
                      inputAccessoryViewID={inputAccessoryViewID}
                      ref={ref[8] as any}
                      onFocus={() => setCurrentInputIndex(8)}
                      onSubmitEditing={() => handleSubmit(onSubmit)()}
                      returnKeyType="go"
                      placeholder="12345"
                      {...automationTestingLocators.addPaymentCCDebit.zip}
                    />
                  </View>
                </View>
              </>
            )}
            <View style={{ margin: 10 }} />

            <View style={styles.indicatorContainer}>
              <View
                style={[
                  styles.indicatorStyle,
                  {
                    backgroundColor: showAddBillAddress
                      ? theme.colors.text2
                      : theme.colors.secondary,
                  },
                ]}
              />
              <View
                style={[
                  styles.indicatorStyle,
                  {
                    backgroundColor: showAddBillAddress
                      ? theme.colors.secondary
                      : theme.colors.text2,
                  },
                ]}
              />
            </View>
            <Button
              text={
                showAddBillAddress
                  ? (translate("addPaymentCCDebit.saveAndContinue") as string)
                  : (translate("addPaymentCCDebit.addAddressButton") as string)
              }
              mode="contained"
              onPress={async () => {
                let isValid = await trigger([CARD_HOLDER, CARD_NUMBER, EXP_DATE, CVV, NICKNAME]);
                if (isValid) {
                  setShowAddBillAddress(true);
                }
                if (showAddBillAddress) {
                  isValid = await trigger([
                    STREET_ADDRESS,
                    STREET_ADDRESS_2,
                    CITY,
                    STATE,
                    ZIP,
                    NICKNAME,
                  ]);
                  if (isValid) {
                    handleSubmit(onSubmit)();
                  }
                }
              }}
              textStyle={{ fontFamily: "Lato_700Bold" }}
              loading={loading}
              disabled={loading}
              {...automationTestingLocators.addPaymentCCDebit.continueButton}
            />
          </ScrollView>
        </View>
        <ErrorWarningBottomSheet
          ref={errorPopup}
          title={translate("addPaymentCCDebit.error") as string}
          body={errorMsgPopup}
          height={170}
          alertType={"error"}
          showButtons={false}
          {...automationTestingLocators.addPaymentCCDebit.errorWarningAlert}
        />
      </CustomTag>
    );
  }
);

interface LongRowProps extends ITextInput {
  styles: any;
  setValue: any;
  formName: string;
  errors?: Record<string, any>;
}
const LongRow = forwardRef(
  (
    {
      styles,
      setValue,
      formName,
      placeholder,
      errors,
      render,
      maxLength,
      keyboardType = "default",
      returnKeyType,
      inputAccessoryViewID,
      onFocus,
      onSubmitEditing,
      value,
      isSensitiveInformation,
      ...props
    }: LongRowProps,
    ref: any
  ) => {
    return (
      <View
        accessible={true}
        ref={(x) =>
          isSensitiveInformation
            ? Auryc.markViewAsSensitiveInformation(x)
            : Auryc.unMarkViewAsSensitiveInformation(x)
        }>
        <TextInput
          value={value}
          labelStyle={{ fontFamily: "Lato_700Bold" }}
          label={translate(`addPaymentCCDebit.${formName}`)}
          error={errors?.[formName]}
          mode="outlined"
          render={render}
          keyboardType={keyboardType}
          maxLength={maxLength}
          placeholder={placeholder}
          onChangeText={(text) => setValue(formName, text)}
          ref={ref}
          returnKeyType={returnKeyType}
          inputAccessoryViewID={inputAccessoryViewID}
          onFocus={onFocus}
          onSubmitEditing={onSubmitEditing}
          {...props}
        />
      </View>
    );
  }
);
const CardInformationRow = forwardRef(
  (
    {
      setValue,
      setError,
      styles,
      errors,
      clearErrors,
      inputAccessoryViewID,
      onFocus0,
      onSubmitEditing0,
      onFocus1,
      onSubmitEditing1,
      cvvValue,
      expValue,
      ...props
    }: any,
    ref: any
  ) => {
    const setExpDate = (formName: string, value: any) => {
      clearErrors(formName);
      const now = moment().startOf("month");
      const selected = value.startOf("month");
      const formatted = value.format("MMYY");
      setValue(formName, formatted);
      if (selected < now) {
        setError(formName, { type: "invalid", message: "Invalid expiry date" });
      }
    };

    return (
      <View style={styles.cardInformationRow}>
        <View style={{ width: "48%" }} ref={(x) => Auryc.markViewAsSensitiveInformation(x)}>
          <MonthYearDropdownNew
            value={expValue}
            title={translate("addPaymentCCDebit.expirationDate")}
            setValue={setExpDate}
            formName={EXP_DATE}
            setError={setError}
            label={Platform.OS === "ios" ? undefined : "Date"}
            errors={errors}
            ref={ref[0]}
            inputAccessoryViewID={inputAccessoryViewID}
            onSubmitEditing={onSubmitEditing0}
            {...automationTestingLocators.addPaymentCCDebit.expDate}
            {...props}
          />
        </View>
        <View
          style={{ width: "48%" }}
          accessible={true}
          ref={(x) => Auryc.markViewAsSensitiveInformation(x)}>
          <TextInput
            value={cvvValue}
            label={translate("addPaymentCCDebit.cvv")}
            placeholder={"123"}
            labelStyle={{ fontFamily: "Lato_700Bold" }}
            error={errors?.[CVV]}
            keyboardType="number-pad"
            maxLength={3}
            mode="outlined"
            onChangeText={(text) => setValue(CVV, text)}
            ref={ref[1]}
            inputAccessoryViewID={inputAccessoryViewID}
            onFocus={onFocus1}
            onSubmitEditing={onSubmitEditing1}
            {...automationTestingLocators.addPaymentCCDebit.cvv}
            {...props}
          />
        </View>
      </View>
    );
  }
);
